/**
 * Admin Studio v1 — AdminFabContext
 * Global FAB state with ownership guard to prevent leaks during fast navigation.
 */
import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';

export type AdminFabVisibility = 'mobile' | 'all';

export interface AdminFabConfig {
  /** Unique id per page, e.g. 'admin-experiences-create' */
  id: string;
  ariaLabel: string;
  label?: string;
  icon?: ReactNode;
  onClick: () => void;
  visibility?: AdminFabVisibility;
  disabled?: boolean;
}

interface AdminFabContextValue {
  config: AdminFabConfig | null;
  setFab: (config: AdminFabConfig) => void;
  clearFab: (ownerId: string) => void;
}

const AdminFabContext = createContext<AdminFabContextValue | null>(null);

export function AdminFabProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AdminFabConfig | null>(null);
  const ownerRef = useRef<string | null>(null);

  const setFab = useCallback((c: AdminFabConfig) => {
    if (process.env.NODE_ENV === 'development' && ownerRef.current && ownerRef.current !== c.id) {
      console.warn(`[AdminFab] Overwriting FAB "${ownerRef.current}" with "${c.id}". Previous cleanup missing?`);
    }
    ownerRef.current = c.id;
    setConfig(c);
  }, []);

  const clearFab = useCallback((ownerId: string) => {
    // Ownership guard: only clear if the caller owns the current config
    if (ownerRef.current === ownerId) {
      ownerRef.current = null;
      setConfig(null);
    }
  }, []);

  return (
    <AdminFabContext.Provider value={{ config, setFab, clearFab }}>
      {children}
    </AdminFabContext.Provider>
  );
}

export function useAdminFab() {
  const ctx = useContext(AdminFabContext);
  if (!ctx) throw new Error('useAdminFab must be used within AdminFabProvider');
  return ctx;
}
